import React from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import {
  Theme,
  Grid,
  Card,
  CardContent,
  Divider,
  Link,
} from '@material-ui/core'
import Container from '@material-ui/core/Container'
import messages from '../messages'
import { PRODUCTS } from './Products'
import * as HelpInfo from '@src/routes/Configurators/utils/HelpInfo.json'

import clsx from 'clsx'
import {
  standardTubeRoute,
  dnaPlatesRoute,
  fastDuplexOligosRoute,
  useRouter,
} from '@src/routes'
import { Link as LinkComponent } from '@src/components/Link'
import {
  CONTACT_LAB_WATER_SPECIALIST_URL,
  CONTACT_MILLQSQ_SPECIALIST_URL,
} from '../components/ConfiguratorStepper/ShoppingHelp/shopping-help'

const useStyles = makeStyles((theme: Theme) => ({
  headingFont: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
  },
  descriptionFont: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
  },
  descriptionFontLongText: {
    fontSize: theme.typography.pxToRem(10),
    fontWeight: theme.typography.fontWeightBold,
  },
  regularFont: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightRegular,
  },
  widthFitContent: {
    width: 206,
  },
  tableBody: {
    paddingBottom: theme.spacing(0.5),
  },
  mgTop4: {
    marginTop: theme.spacing(4),
  },
  mgTop1: {
    marginTop: theme.spacing(1),
  },
  mgTop8: {
    marginTop: theme.spacing(8),
  },
  mgBottom1: {
    marginBottom: theme.spacing(1),
  },
  borderBottom0: {
    borderBottom: 0,
  },
  productCategory: {
    display: 'flex',
  },
  boxCss: {
    backgroundColor: 'white',
    padding: theme.spacing(4, 5),
    marginBottom: theme.spacing(4),
    borderRadius: 0,
    boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
  },
}))

//  if productCode  is 1 i.e product is unauthorized
//                     2 i.e product is invalid
interface PageErrorProps {
  productCode: number
  country: string
}

const SimilarProductData = [
  {
    displayName: 'CONF_DNA_OLIGOS_IN_TUBES_standard',
    url: `${standardTubeRoute.index()}?product=standard`,
  },
  {
    displayName: 'CONF_TITLE_dnaplate',
    url: `${dnaPlatesRoute.index()}?product=dnaplate`,
  },
  {
    displayName: '_PC_SIRNA_TITLE',
    url: `${fastDuplexOligosRoute.index()}?product=duplex`,
  },
  {
    displayName: 'CONF_MILLIPLEX_LUMINEX_IMMUNOASSAYS',
    url: '/products/protein-biology/immunoassay-platform-solutions/milliplex-multiplex-assays-for-luminex-technology',
  },
  {
    displayName: 'MILLI_Q_WATER_PURIFICATION_SYSTEMS',
    url: '/products/water-purification/milli-q-benchtop-water-purification-systems',
  },
]

const ConfiguratorPageError: React.FC<PageErrorProps> = (props) => {
  const classes = useStyles()
  const router = useRouter()
  const { productCode, country } = props
  const { language } = router.query
  const defaultEmail = 'custsvc@milliporesigma.com'
  const defaultPhone = '1-800-234-5362'
  const isLabwaterRoute = router.pathname === '/[country]/[language]/lab-water'
  const isMilliQSqRoute =
    router.pathname === '/[country]/[language]/configurators/milli-q-sq'

  const email = () => {
    if (HelpInfo[`CONF_EMAIL_ADDRESS_${country}_default`]) {
      return HelpInfo[`CONF_EMAIL_ADDRESS_${country}_default`]
    } else {
      return defaultEmail
    }
  }
  const phoneNumber = () => {
    if (HelpInfo[`CONF_TEL_${country}_default`]) {
      return HelpInfo[`CONF_TEL_${country}_default`]
    }
    return defaultPhone
  }

  const registeredProducts = [
    'CONF_MILLIPLEX_LUMINEX_IMMUNOASSAYS',
    'MILLI_Q_WATER_PURIFICATION_SYSTEMS',
  ]

  const displaySimilarProducts = () => {
    return SimilarProductData.map((data, index) => (
      <div key={index}>
        <Link href={data.url} className={clsx(classes.descriptionFont)}>
          {registeredProducts.includes(data.displayName) ? (
            <FormattedMessage
              {...messages[data.displayName]}
              values={{
                registered: (
                  <sup>
                    <FormattedMessage {...messages.CONF_TRADEMARK} />
                  </sup>
                ),
              }}
            />
          ) : (
            <FormattedMessage {...messages[data.displayName]} />
          )}
        </Link>
      </div>
    ))
  }

  const displayProductCategory = () => {
    if (PRODUCTS) {
      return PRODUCTS.map((data, index) => {
        return (
          <Grid item sm={3} key={index}>
            <div>
              <div
                className={clsx(
                  classes.descriptionFont,
                  classes.tableBody,
                  classes.widthFitContent
                )}
              >
                <FormattedMessage {...data.label} />
              </div>

              {data.items
                ? data.items.map((item, position) => (
                    <div key={position}>
                      <div
                        className={clsx(
                          classes.borderBottom0,
                          classes.tableBody,
                          classes.widthFitContent
                        )}
                      >
                        <Link
                          href={item.url}
                          className={classes.descriptionFont}
                        >
                          <FormattedMessage {...item.label} />
                        </Link>
                      </div>
                    </div>
                  ))
                : ''}
            </div>
          </Grid>
        )
      })
    } else {
      return ''
    }
  }

  return (
    <Container>
      <Grid container spacing={5}>
        <Grid item xs={9}>
          {productCode === 1 ? (
            <>
              <Grid className={classes.headingFont}>
                <FormattedMessage
                  {...messages.CONF_UNAUTHORIZED_TEXT_DESC_V2}
                />
              </Grid>
              <Grid className={classes.regularFont}>
                <span>
                  <FormattedMessage
                    {...messages.CONF_UNAUTHORIZED_DESC}
                    values={{
                      link: (
                        <Link
                          href="/register"
                          className={classes.descriptionFont}
                        >
                          <FormattedMessage {...messages.CONF_REGISTER} />
                        </Link>
                      ),
                    }}
                  />
                </span>
              </Grid>
            </>
          ) : (
            <>
              <Grid className={classes.headingFont}>
                {productCode === 3 ? (
                  <FormattedMessage {...messages.CONF_PRODUCT_NOT_VALID} />
                ) : productCode === 2 ? (
                  <FormattedMessage {...messages.CONF_INVALID_PRODUCT} />
                ) : (
                  ''
                )}
              </Grid>
              {productCode === 3 ? (
                ''
              ) : productCode === 2 ? (
                <Grid className={classes.regularFont}>
                  <span>
                    <FormattedMessage
                      {...messages.CONF_INVALID_PRODUCT_DESC_1}
                    />
                    &nbsp;
                  </span>
                  <a
                    href={'mailto:webhelp@sial.com'}
                    className={classes.descriptionFont}
                  >
                    <FormattedMessage {...messages.CONF_CUSTOMER_SUPPORT} />
                  </a>
                  &nbsp;
                  <span>
                    <FormattedMessage
                      {...messages.CONF_INVALID_PRODUCT_DESC_2}
                    />
                  </span>
                </Grid>
              ) : (
                <Grid className={classes.headingFont}>
                  <span>
                    <FormattedMessage
                      {...messages.CONF_USER_CANNOT_ORDER}
                      values={{
                        link: (
                          <Link
                            href="/collections/offices"
                            className={classes.descriptionFont}
                          >
                            <FormattedMessage {...messages.CONTACT} />
                          </Link>
                        ),
                      }}
                    />
                  </span>
                </Grid>
              )}
              {isLabwaterRoute && (
                <Grid className={classes.regularFont}>
                  <FormattedMessage
                    {...messages.LAB_WATER_REQUEST}
                    values={{
                      link: (
                        <LinkComponent
                          href={`/${country}/${language}/${CONTACT_LAB_WATER_SPECIALIST_URL}`}
                          passHref
                        >
                          <Link>
                            <FormattedMessage
                              {...messages.CONTACT_LAB_WATER_SPECIALIST}
                            />
                          </Link>
                        </LinkComponent>
                      ),
                    }}
                  />
                </Grid>
              )}
              {isMilliQSqRoute && (
                <Grid className={classes.regularFont}>
                  <FormattedMessage
                    {...messages.LAB_WATER_REQUEST}
                    values={{
                      link: (
                        <LinkComponent
                          href={`/${country}/${language}/${CONTACT_MILLQSQ_SPECIALIST_URL}`}
                          passHref
                        >
                          <Link>
                            <FormattedMessage
                              {...messages.CONTACT_LAB_WATER_SPECIALIST}
                            />
                          </Link>
                        </LinkComponent>
                      ),
                    }}
                  />
                </Grid>
              )}
            </>
          )}

          <Grid className={clsx(classes.mgTop8)}>
            <div className={classes.headingFont}>
              <FormattedMessage {...messages.CONF_SIMILAR_PRODUCTS} />
            </div>
            {displaySimilarProducts()}
          </Grid>
          <Grid className={clsx(classes.mgTop8)}>
            <div className={clsx(classes.headingFont, classes.mgBottom1)}>
              <FormattedMessage {...messages.CONF_SHOP_PRODUCT_CATEGORIES} />
            </div>
            <div className={classes.productCategory}>
              {displayProductCategory()}
            </div>
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <Card className={classes.boxCss}>
            <CardContent>
              <Grid>
                <div className={classes.headingFont}>
                  <FormattedMessage {...messages.CONTACT_US} />
                  <Divider className={classes.mgBottom1} />
                </div>
                <div>
                  <Link href="/service" className={classes.descriptionFont}>
                    <FormattedMessage {...messages.CONF_CUSTOMER_SUPPORT} />
                  </Link>
                </div>
                <div className={classes.regularFont}>
                  <FormattedMessage {...messages.PHONE} /> : {phoneNumber()}
                </div>
                <div className={classes.regularFont}>
                  <FormattedMessage {...messages.CART_EMAIL} /> : {email()}
                </div>
                <div className={clsx(classes.mgTop4, classes.headingFont)}>
                  <FormattedMessage {...messages.CONF_PRODUCT_SUGGESTION} />
                  <Divider className={classes.mgBottom1} />
                </div>
                <div className={classes.regularFont}>
                  <span>
                    <FormattedMessage
                      {...messages.CONF_PRODUCT_SUGGESTION_DESC_1}
                    />
                    &nbsp;
                  </span>
                  <Link href="#">
                    <FormattedMessage {...messages.CONF_MAKE_A_SUGGESTION} />
                  </Link>
                  &nbsp;
                  <span>
                    <FormattedMessage
                      {...messages.CONF_PRODUCT_SUGGESTION_DESC_2}
                    />
                  </span>
                </div>
                <div className={clsx(classes.mgTop4, classes.headingFont)}>
                  <FormattedMessage {...messages.CONF_MARKET_SELECT} />
                  <Divider className={classes.mgBottom1} />
                </div>
                <div className={classes.regularFont}>
                  <span>
                    <FormattedMessage {...messages.CONF_VISIT} /> &nbsp;
                  </span>
                  <Link href="#">
                    <FormattedMessage
                      {...messages.CONF_ALDRICH_MARKET_SELECT}
                    />
                  </Link>
                  &nbsp;
                  <span>
                    <FormattedMessage {...messages.CONF_MARKET_SELECT_DESC} />
                  </span>
                </div>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default ConfiguratorPageError
